import { Injectable } from '@angular/core';
import { API } from './api';
import { AssetProvider, ClassCountsMap, TaggableAsset } from './asset';
import { BaseProvider } from './base';
import { BrainVersionType } from './brain';

const MAX_FRAMES_TO_RETURN = 9999;

export class RecognitionFrame {
    assetIdentifier: string;
    name: string;
    prefix: string;
    testOutcome: string | null;
    signedThumbnailUrl: string;
    predictedClass: string;
    actualClass: string;
    normalScore: number;
    anomalyOverlaySignedUrl: string | null;
}

export interface DetectionFrame {
    assetIdentifier: string;
    name: string;
    signedThumbnailUrl: string;
    classGroundTruthCounts: ClassCountsMap;
    classPredictionCounts: ClassCountsMap;
    groundTruthCount: number;
    predictionCount: number;
    dateValidate: Date;
}

export interface DetectionFramesResponse {
    total: number;
    validationFrames: DetectionFrame[];
}

export interface DetectionFrameDetailGT {
    className: string;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

export interface DetectionFrameDetailPrediction {
    className: string;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    confidence: number;
}

export interface DetectionFrameDetail {
    success: boolean;
    name: string;
    thumbSignedUrl: string;
    signedUrl: string;
    groundTruths: DetectionFrameDetailGT[];
    predictions: DetectionFrameDetailPrediction[];
}

@Injectable()
export class FrameProvider extends BaseProvider {
    constructor(public api: API, public assetProvider: AssetProvider) {
        super();
    }

    public async getValidationRecognitionFrames(
        id: string,
        offset: number = 0,
        limit: number = 9999,
        brainVersionType: BrainVersionType = BrainVersionType.Learning
    ): Promise<RecognitionFrame[]> {
        const url = `datasets/${id}/validation-frames?offset=${offset}&limit=${limit}&brainVersionType=${brainVersionType}`;

        const response = await this.debounce(
            url,
            () => {
                return this.api.get(url);
            },
            250
        );

        return response.validationFrames;
    }

    public async getTestRecognitionFrames(
        datasetIdentifier: string,
        offset?: number,
        limit?: number
    ): Promise<RecognitionFrame[]> {
        const url = `datasets/${datasetIdentifier}/test-set/recognition-frames?offset=${offset || 0}&limit=${limit ||
            MAX_FRAMES_TO_RETURN}`;

        const response = await this.debounce(
            url,
            () => {
                return this.api.get(url);
            },
            250
        );

        return response.message.testAssetResults;
    }

    public async getTestRecognitionFrameDetail(
        datasetIdentifier: string,
        testAssetIdentifier: string
    ): Promise<TaggableAsset> {
        const url = `datasets/${datasetIdentifier}/test-set/recognition-frames/${testAssetIdentifier}`;

        const response = await this.debounce(
            url,
            () => {
                return this.api.get(url);
            },
            250
        );

        if (response.success && response.message) {
            return this.assetProvider.convertAssetResponseObjectToTaggableAsset(response.message);
        } else {
            throw new Error(`No test asset found with identifier: ${testAssetIdentifier}`);
        }
    }

    public async getValidationDetectionFramesResponse(
        id: string,
        offset: number,
        limit: number,
        groundTruthClass: string | undefined,
        predictionClass: string | undefined
    ): Promise<DetectionFramesResponse> {
        let url = `datasets/${id}/detection-validation-frames?offset=${offset}&limit=${limit}`;

        if (groundTruthClass) {
            url += `&groundTruthClass=${groundTruthClass}`;
        }

        if (predictionClass) {
            url += `&predictionClass=${predictionClass}`;
        }

        try {
            const response = await this.debounce(
                url,
                () => {
                    return this.api.get(url);
                },
                250
            );

            return {
                total: response.total,
                validationFrames: response.validationFrames
            };
        } catch (err) {
            throw err;
        }
    }

    public async getValidationDetectionFrameDetail(
        datasetIdentifier: string,
        assetIdentifier: string
    ): Promise<DetectionFrameDetail> {
        return await this.api.get(`datasets/${datasetIdentifier}/detection-validation-frames/${assetIdentifier}`);
    }

    public async getTestDetectionFrames(
        datasetIdentifier: string,
        limit?: number,
        offset?: number,
        groundTruthClass?: string,
        predictionClass?: string
    ): Promise<DetectionFrame[]> {
        const response = await this.getTestDetectionFramesResponse(
            datasetIdentifier,
            offset,
            limit,
            groundTruthClass,
            predictionClass
        );

        return response.validationFrames;
    }

    public async getTestDetectionFramesResponse(
        datasetIdentifier: string,
        offset?: number,
        limit?: number,
        groundTruthClass?: string,
        predictionClass?: string
    ): Promise<DetectionFramesResponse> {
        let url = `datasets/${datasetIdentifier}/test-set/detection-frames?offset=${offset || 0}&limit=${limit ||
            MAX_FRAMES_TO_RETURN}`;

        if (groundTruthClass) {
            url += `&groundTruthClass=${groundTruthClass}`;
        }

        if (predictionClass) {
            url += `&predictionClass=${predictionClass}`;
        }

        const response = await this.debounce(
            url,
            () => {
                return this.api.get(url);
            },
            250
        );

        return {
            total: response.message.total,
            validationFrames: response.message.testAssetResults
        };
    }

    public async getTestDetectionFrameDetail(
        datasetIdentifier: string,
        assetIdentifier: string
    ): Promise<DetectionFrameDetail> {
        return await this.api.get(`datasets/${datasetIdentifier}/test-set/detection-frames/${assetIdentifier}`);
    }

    public async getDetectionFrameDetail(
        datasetIdentifier: string,
        assetIdentifier: string,
        isTestSet: boolean = false
    ): Promise<DetectionFrameDetail> {
        if (isTestSet) {
            return await this.getTestDetectionFrameDetail(datasetIdentifier, assetIdentifier);
        } else {
            return await this.getValidationDetectionFrameDetail(datasetIdentifier, assetIdentifier);
        }
    }
}



// WEBPACK FOOTER //
// ./src/providers/frame.ts